import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { DataPerformanceSummaryTrend, GoalStatus } from 'webapp/enums/enums';
import { getPreviewChangeIndicatorClass } from 'webapp/utils/data';
import { formatNumber, getIntervalTime } from 'webapp/utils/number';

import type { Goal } from 'webapp/models-custom';
import type Block from 'webapp/models-custom/block';
import DateRange from 'webapp/models-custom/date-range';
import Serviced from 'webapp/models-custom/serviced';
import StatusObject from 'webapp/models-custom/status-object';

export default class GoalData extends Serviced {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('timer') declare timerService: Service.Timer;
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;

	goal: Goal;
	blockId: number;

	constructor(goal: Goal, blockId: number) {
		super();
		this.goal = goal;
		this.blockId = blockId;
	}

	@cached
	get block(): Block | null {
		const id = this.blockId;
		if (id) {
			return this.mmBlocksService.findModel(String(id))! || null;
		}
		return null;
	}

	get dateRange() {
		return this.block?.dateRangeModel || null;
	}

	get data() {
		return this.block?.data;
	}

	get numberDataRoot() {
		return this.block?.dataSelects[0]?.dataRoot;
	}

	get output() {
		return this.numberDataRoot?.outputs[0];
	}

	get dataPoint() {
		return this.numberDataRoot?.outputs[0]?.point;
	}

	@cached
	get primaryPoint() {
		const output = this.output,
			dataPoint = this.dataPoint;
		if (output && dataPoint) {
			return this.mmSamplesService.createDChartPointFromPoint(this.output, this.dataPoint);
		} else {
			return null;
		}
	}

	get comparePoint() {
		return this.primaryPoint?.comparePoints[0];
	}

	get isCompareEmpty() {
		return !this.comparePoint?.changeStr;
	}

	get hasValue() {
		return !!this.primaryPoint?.hasValue;
	}

	get goalExtra() {
		return this.dataPoint?.comparePoints[0]?.extra;
	}

	get expectedValue() {
		// returned with compare point
		return this.goalExtra?.goalExpectedValue || null;
	}

	get expectedValueStr() {
		// returned with compare point
		return this.goalExtra?.goalExpectedValueStr || '';
	}

	get targetGranulation() {
		// returned with compare point
		return this.goalExtra?.goalTargetGranulation || null;
	}

	get status() {
		// returned with compare point
		return this.goalExtra?.goalStatus || null;
	}

	get statusText() {
		return this.mmGoalsService.getStatusText(this.status);
	}

	get statusClass() {
		return this.mmGoalsService.getStatusClass(this.status);
	}

	get targetGranulationTextLy() {
		const granulation = this.targetGranulation;
		if (!granulation) {
			return '';
		}
		return DateRange.getTimeUnitText(granulation, true, true);
	}

	get currentValueStr() {
		if (this.isLoadingData) {
			return '';
		}
		return this.primaryPoint?.valueStr || '-';
	}

	@cached
	get expectedPercent() {
		if (!this.status || this.goal.isManualGoal) {
			return undefined;
		}

		const goalTarget = this.comparePoint?.value;
		const expectedValue = this.expectedValue;

		if (goalTarget && expectedValue) {
			const percent = (expectedValue / goalTarget) * 100;
			return percent > 100 ? 100 : percent;
		}

		return undefined;
	}

	@cached
	get projection() {
		const value = this.primaryPoint?.value,
			expectedValue = this.expectedValue;

		let compare;

		if (expectedValue && typeof value === 'number') {
			// round to 1 number after comma
			compare = ((value - expectedValue) / Math.abs(expectedValue)) * 100;
			compare = Math.round(compare * 10) / 10;
		}

		return compare;
	}

	@cached
	get projectionFormatted() {
		let str = '';

		const compare = this.projection;
		if (typeof compare === 'number') {
			if (compare < 0) {
				str = '▼ ';
			} else if (compare > 0) {
				str = '▲ ';
			}
			str += formatNumber(Math.abs(compare), '0,0.#') + '%';
		}

		return str;
	}

	get projectionIndicatorClass() {
		return getPreviewChangeIndicatorClass(this.projectionFormatted, !!this.output?.invert);
	}

	get currentProgressClass() {
		switch (this.status) {
			case GoalStatus.ON_TRACK:
			case GoalStatus.ACHIEVED:
				return 'done';
			case GoalStatus.AT_RISK:
			case GoalStatus.PARTIAL:
				return 'medium';
			case GoalStatus.OFF_TRACK:
			case GoalStatus.MISSED:
				return 'low';
			default:
				return 'none';
		}
	}

	get currentProgressStyle() {
		// for goal compares, change is just percentage number
		const width = Math.round(Math.min(100, Math.max(1, this.comparePoint?.change || 0)));
		return htmlSafe(`width: ${width}%;`);
	}

	get isComplete() {
		return this.mmGoalsService.getIsGoalComplete(this.status);
	}

	@cached
	get timeLeft() {
		this.timerService.observe();

		const ts = this.goal.valuesLastDateTs || this.primaryPoint?.timestampEnd;
		if (ts) {
			return ts - this.timerService.currentTs;
		}
		return null;
	}

	@cached
	get isAnyTimeLeft() {
		return !this.timeLeft || this.timeLeft > 0;
	}

	@cached
	get timeLeftText() {
		let text = '';
		if (this.isAnyTimeLeft) {
			const timeLeft = this.timeLeft;
			if (timeLeft) {
				text = `${getIntervalTime(timeLeft, 'seconds', true)} left`;
			}
		}
		return text;
	}

	get trend() {
		const projection = this.projection;
		if (typeof projection === 'number') {
			return this.mmDataPerformanceSummariesService.getTrend(
				projection,
				this.projectionIndicatorClass,
			);
		}
		return DataPerformanceSummaryTrend.UNAVAILABLE;
	}

	get lastSync() {
		return this.block?.lastSync || null;
	}

	get statusObjects(): StatusObject[] {
		return this.block?.statusObjects || [];
	}

	@cached
	get dataErrorStatusObjects() {
		return StatusObject.eliminateDuplicates(
			this.statusObjects.filter((item) => !item.isSync && !item.isMediaSync),
			true,
		);
	}

	get showDataStatusIcon() {
		return !!this.lastSync || this.dataErrorStatusObjects.length > 0;
	}

	get isSyncing() {
		return !!this.block?.isSyncing;
	}

	get isDataIncomplete(): boolean {
		return !!this.block?.isDataIncomplete;
	}

	get isLoadingData() {
		return !this.data;
	}
}
